/* A row's location is the PAIR (group_id, section_key) with exactly one side set, and both sides
   must be written on every move: carrying one over from the row's previous location leaves it
   claiming both, which matches neither and renders nowhere until the server snapshot lands. */
const toLocation = (operation) => ({ groupId: operation?.groupId || null, sectionKey: operation?.groupId ? null : operation?.sectionKey || null });

const applySidebarOrganizationOperation = ({ snapshot, operation } = {}) => {
  const current = snapshot || { sidebarType: 'home', groups: [], folders: [], placements: [] };

  if (operation?.action === 'rename_group') {
    return { ...current, groups: current.groups.map((group) => (group.id === operation.groupId ? { ...group, name: operation.name } : group)) };
  }

  if (operation?.action === 'rename_folder') {
    return { ...current, folders: current.folders.map((folder) => (folder.id === operation.folderId ? { ...folder, name: operation.name } : folder)) };
  }

  if (operation?.action === 'set_folder_theme') {
    return { ...current, folders: current.folders.map((folder) => (folder.id === operation.folderId ? { ...folder, themeId: operation.themeId } : folder)) };
  }

  /* Analytics pause: the row dims at once; the server's stamp (kept from the first pause) lands
     with the snapshot. Resuming clears it. */
  if (operation?.action === 'set_folder_pause') {
    return { ...current, folders: current.folders.map((folder) => (folder.id === operation.folderId ? { ...folder, pausedOn: operation.paused ? folder.pausedOn || new Date().toISOString() : null } : folder)) };
  }

  if (operation?.action === 'delete_group') {
    return {
      ...current,
      groups: current.groups.filter((group) => group.id !== operation.groupId),
      folders: current.folders.filter((folder) => folder.groupId !== operation.groupId),
      placements: current.placements.filter((placement) => placement.groupId !== operation.groupId)
    };
  }

  if (operation?.action === 'reorder_groups') {
    const seqById = new Map((operation.groupIds || []).map((groupId, index) => [groupId, index + 1]));
    return { ...current, groups: current.groups.map((group) => ({ ...group, seq: seqById.get(group.id) || group.seq })).sort((left, right) => left.seq - right.seq) };
  }

  if (operation?.action === 'move_to_unsorted') {
    return { ...current, placements: current.placements.filter((placement) => placement.itemType !== operation.itemType || placement.itemId !== operation.itemId) };
  }

  if (operation?.action === 'clear_section_order') {
    return { ...current, placements: current.placements.filter((placement) => !(placement.sectionKey === operation.sectionKey && !placement.folderId)) };
  }

  /* Deleting a folder releases its contents to the level above rather than taking them with it,
     the same way the server does — so the rows stay visible through the round trip instead of
     blinking out and back. Positions settle from the response. */
  if (operation?.action === 'delete_folder') {
    const target = current.folders.find((folder) => folder.id === operation.folderId) || null;

    if (!target) {
      return current;
    }

    const parentFolderId = target.parentFolderId || null;

    /* The server releases mixed children to their own typed roots. This snapshot has locations,
       not page kinds; keep it until the response rather than guessing from sectionKey. */
    if (current.sidebarType === 'analytics' && target.sectionKey && !parentFolderId) {
      return current;
    }

    return {
      ...current,
      folders: current.folders.filter((folder) => folder.id !== target.id).map((folder) => (folder.parentFolderId === target.id ? { ...folder, parentFolderId } : folder)),
      placements: current.placements.map((placement) => (placement.folderId === target.id ? { ...placement, folderId: parentFolderId } : placement))
    };
  }

  if (operation?.action === 'reorder_level') {
    const parentFolderId = operation.parentFolderId || null;
    const location = toLocation(operation);
    const folderSeqById = new Map();
    const itemSeqByKey = new Map();

    (operation.order || []).forEach((entry, index) => {
      if (entry.kind === 'folder') {
        folderSeqById.set(entry.id, index + 1);
      } else {
        itemSeqByKey.set(`${entry.itemType}:${entry.itemId}`, { itemType: entry.itemType, itemId: entry.itemId, seq: index + 1 });
      }
    });

    const movedFolderIds = new Set(folderSeqById.keys());
    let discovered = true;
    while (discovered) {
      discovered = false;
      for (const folder of current.folders) {
        if (movedFolderIds.has(folder.parentFolderId) && !movedFolderIds.has(folder.id)) {
          movedFolderIds.add(folder.id);
          discovered = true;
        }
      }
    }

    const nextFolders = current.folders.map((folder) => {
      if (folderSeqById.has(folder.id)) {
        return { ...folder, ...location, parentFolderId, seq: folderSeqById.get(folder.id) };
      }
      return movedFolderIds.has(folder.id) ? { ...folder, ...location } : folder;
    });
    const nextPlacements = current.placements.filter((placement) => !itemSeqByKey.has(`${placement.itemType}:${placement.itemId}`)).map((placement) => (movedFolderIds.has(placement.folderId) ? { ...placement, ...location } : placement));

    for (const { itemType, itemId, seq } of itemSeqByKey.values()) {
      const previous = current.placements.find((placement) => placement.itemType === itemType && placement.itemId === itemId) || {};
      nextPlacements.push({ sidebarType: current.sidebarType, ...previous, ...location, itemType, itemId, folderId: parentFolderId, seq });
    }

    return { ...current, folders: nextFolders, placements: nextPlacements };
  }

  return current;
};

export default applySidebarOrganizationOperation;
